/**
 * กล่องดูตัวอย่างหน้าเว็บจริงในหลังบ้าน
 *
 * ใช้ร่วมกันทั้ง widget, บล็อก และบทความ — ฝัง iframe ของ URL สาธารณะตัวจริง
 * จึงเห็นตรงกับที่ผู้ชมเห็น 100% ไม่ใช่ภาพจำลอง
 *
 * ทำไมถึงแทนที่ปุ่ม "เปิดในแท็บใหม่" เดิม: การเด้งแท็บใหม่ทำให้เสียบริบท
 * ต้องสลับหน้าต่างไปมาแล้วหาที่เดิมให้เจอ ดูในกล่องแล้วกด Escape กลับมาที่เดิม
 * ได้ทันทีเร็วกว่ามาก — แต่ยังเก็บปุ่มเปิดแท็บใหม่ไว้ในกล่องสำหรับคนที่อยากดูเต็มจอ
 */
import { useState, type ReactNode } from 'react';
import { Button, Loading, Modal, IconRefresh, IconExternal } from './ui/index.js';

export function PagePreviewModal({
  open, onClose, title, url, meta, footerExtra, height = 520, unavailable,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** URL สาธารณะที่จะฝัง — null เมื่อยังโหลดไม่เสร็จ */
  url: string | null;
  /** แถวข้อมูลย่อยด้านบน เช่น badge บอกชนิด */
  meta?: ReactNode;
  /** ปุ่มเพิ่มเติมใน footer เช่น "ไปตั้งค่า" */
  footerExtra?: ReactNode;
  height?: number;
  /** ข้อความแทน iframe เมื่อหน้านั้นยังไม่เปิดสาธารณะ */
  unavailable?: ReactNode;
}) {
  // เปลี่ยนค่านี้ = React สร้าง iframe ใหม่ = โหลดซ้ำ
  const [reloadAt, setReloadAt] = useState(0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          {url && !unavailable && (
            <Button
              variant="ghost"
              icon={<IconExternal size={14} />}
              onClick={() => window.open(url, '_blank', 'noopener')}
            >
              เปิดในแท็บใหม่
            </Button>
          )}
          {footerExtra}
        </>
      }
    >
      <div className="space-y-3">
        {(meta || (url && !unavailable)) && (
          <div className="flex flex-wrap items-center gap-2">
            {meta}
            {url && !unavailable && (
              <Button
                size="sm" variant="ghost" className="ms-auto"
                icon={<IconRefresh size={14} />}
                onClick={() => setReloadAt((n) => n + 1)}
              >
                รีเฟรช
              </Button>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--line)' }}>
          {unavailable ? (
            <div className="p-10 text-center text-sm text-[var(--ink-3)]">{unavailable}</div>
          ) : url ? (
            <iframe
              key={`${reloadAt}-${url}`}
              src={url}
              title={title}
              className="w-full border-0 bg-[var(--sunken)]"
              style={{ height }}
              /*
               * allow-same-origin จำเป็นจริง ไม่ใช่ความมักง่าย: หน้าที่ฝังเรียก API
               * ของเราเองซึ่งตรวจ Origin ถ้า sandbox ทำให้กลายเป็น origin null
               * คำขอจะโดนปฏิเสธและกล่องจะว่างเปล่า — เนื้อหาเป็นของระบบเราเอง
               * บนโดเมนเดียวกัน ไม่ใช่ของบุคคลที่สาม
               */
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          ) : (
            <div style={{ height }}><Loading label="" /></div>
          )}
        </div>
      </div>
    </Modal>
  );
}
